import { useMemo, useRef } from "react"
import { Printer, Download, GraduationCap, Target, Award, Brain, Briefcase, Star, BookOpen } from "lucide-react"
import { Sidebar } from "../components/ui/sidebar"
import ProtectedRoute from "../components/ProtectedRoute"
import { StorageService } from "@/hooks/services/storageService"
import { AuthStore } from "@/hooks/stores/AuthStore"
import { getAllNodes } from "@/data/career-tree"

// ─── Helpers ──────────────────────────────────────────────────────────────────

function useStudentData() {
  return useMemo(() => {
    const currentUser = AuthStore.getState().currentUser
    const students = StorageService.read("students") ?? []
    const student = students.find(s => s.userId === currentUser?._id || s.email === currentUser?.email) ?? students[0]
    if (!student) return null

    const prefs = student.preferences ?? {}
    const goals = (StorageService.read("goals") ?? []).filter(g =>
      (g.userId === student.userId || g.userId === student._id) && g.status === "completed"
    )
    const achievements = (student.achievements ?? []).filter(a => a.unlocked)
    const activityLog = student.activityLog ?? []

    const traitProfile = student.traitProfile ?? {}
    const topTraits = Object.entries(traitProfile)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)

    const allNodes = getAllNodes().filter(n => n.level === "field")
    const careerPaths = (student.selectedBroadPaths ?? []).map(pathId => {
      const match = allNodes.find(n => n.parentId === pathId)
      return pathId.replace("broad_", "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
    })

    const selfReported = activityLog.filter(e => e.eventCategory === "self_reported")
    const extracurriculars = selfReported
      .filter(e => ["extracurricular", "club", "competition", "volunteering"].includes(e.eventType))
      .map(e => e.meta?.activityName ?? e.meta?.clubName ?? e.meta?.name ?? e.eventType)
      .filter(Boolean)

    const examResults = activityLog.filter(e => e.eventType === "exam_result_uploaded")
      .map(e => ({ subject: e.meta?.subject, grade: e.meta?.grade, exam: e.meta?.examType }))
      .filter(e => e.subject)

    return {
      name: `${student.firstName ?? student.name ?? "Student"} ${student.lastName ?? ""}`.trim(),
      email: currentUser?.email ?? student.email ?? "",
      classLevel: prefs.classLevel ?? student.classLevel ?? "",
      examBody: prefs.examBody ?? "",
      country: prefs.country ?? "",
      stream: prefs.stream ?? "",
      careerPaths,
      topTraits,
      goals: goals.map(g => g.title ?? g.name ?? "Goal"),
      achievements: achievements.map(a => a.title ?? a.name ?? "Achievement"),
      extracurriculars: [...new Set(extracurriculars)],
      examResults,
    }
  }, [])
}

// ─── CV sections ──────────────────────────────────────────────────────────────

function CVSection({ title, icon: Icon, children }) {
  return (
    <div className="mb-6 print:mb-5">
      <div className="flex items-center gap-2 mb-3 pb-1.5 border-b-2 border-slate-200 print:border-slate-300">
        <Icon className="w-4 h-4 text-indigo-600 print:text-slate-700" />
        <h2 className="text-sm font-black text-slate-700 uppercase tracking-widest">{title}</h2>
      </div>
      {children}
    </div>
  )
}

function Tag({ children }) {
  return (
    <span className="inline-block bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-semibold px-2.5 py-1 rounded-lg print:bg-slate-100 print:border-slate-200 print:text-slate-700">
      {children}
    </span>
  )
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function CVBuilderPage() {
  const data = useStudentData()
  const printRef = useRef(null)

  const handlePrint = () => window.print()

  if (!data) {
    return (
      <ProtectedRoute>
        <div className="flex min-h-screen bg-slate-100">
          <Sidebar />
          <main className="flex-1 lg:ml-64 flex items-center justify-center p-8">
            <div className="text-center space-y-3">
              <GraduationCap className="w-12 h-12 text-slate-300 mx-auto" />
              <p className="text-slate-500 font-medium">Complete your profile to generate your CV.</p>
            </div>
          </main>
        </div>
      </ProtectedRoute>
    )
  }

  return (
    <ProtectedRoute>
      <div className="flex min-h-screen bg-slate-100">
        <Sidebar />
        <main className="flex-1 lg:ml-64 p-4 md:p-8 overflow-y-auto">
          <div className="max-w-4xl mx-auto space-y-6">

            {/* Page header (screen only) */}
            <div className="flex items-center justify-between print:hidden">
              <div>
                <h1 className="text-3xl font-black text-slate-900">CV Builder</h1>
                <p className="text-slate-500 text-sm mt-1">Your career profile, ready to print or download.</p>
              </div>
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-5 py-2.5 rounded-xl transition-colors"
              >
                <Printer className="w-4 h-4" />
                Print / Save PDF
              </button>
            </div>

            {/* CV Document */}
            <div ref={printRef} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 print:shadow-none print:border-0 print:rounded-none print:p-0">

              {/* Header */}
              <div className="flex items-start justify-between mb-8 print:mb-6">
                <div>
                  <h1 className="text-3xl font-black text-slate-900 print:text-2xl">{data.name}</h1>
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-500">
                    {data.email && <span>{data.email}</span>}
                    {data.classLevel && <span className="flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5" />{data.classLevel}</span>}
                    {data.examBody && <span>{data.examBody}</span>}
                    {data.country && <span>{data.country}</span>}
                  </div>
                  {data.stream && (
                    <span className="mt-2 inline-block bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-full print:bg-slate-700">
                      {data.stream.charAt(0).toUpperCase() + data.stream.slice(1)} Stream
                    </span>
                  )}
                </div>
                <div className="text-right text-xs text-slate-400 print:text-slate-500">
                  <p className="font-semibold">EarlyScape Career Profile</p>
                  <p>{new Date().toLocaleDateString("en-GB", { year: "numeric", month: "long" })}</p>
                </div>
              </div>

              {/* Career Interests */}
              {data.careerPaths.length > 0 && (
                <CVSection title="Career Interests" icon={Briefcase}>
                  <div className="flex flex-wrap gap-2">
                    {data.careerPaths.map(p => <Tag key={p}>{p}</Tag>)}
                  </div>
                </CVSection>
              )}

              {/* Key Traits */}
              {data.topTraits.length > 0 && (
                <CVSection title="Key Strengths" icon={Brain}>
                  <div className="space-y-2">
                    {data.topTraits.map(([traitId, score]) => {
                      const label = traitId.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
                      return (
                        <div key={traitId} className="flex items-center gap-3">
                          <span className="text-sm font-semibold text-slate-700 w-44 shrink-0">{label}</span>
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${Math.min(100, score)}%` }} />
                          </div>
                          <span className="text-xs text-slate-400 w-10 text-right shrink-0">{Math.round(score)}</span>
                        </div>
                      )
                    })}
                  </div>
                </CVSection>
              )}

              {/* Academic Results */}
              {data.examResults.length > 0 && (
                <CVSection title="Academic Results" icon={BookOpen}>
                  <div className="flex flex-wrap gap-2">
                    {data.examResults.map((r, i) => (
                      <div key={i} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-sm">
                        <span className="font-semibold text-slate-700">{r.subject}</span>
                        <span className="text-slate-400">·</span>
                        <span className="font-bold text-indigo-700">{r.grade}</span>
                        {r.exam && <span className="text-xs text-slate-400">({r.exam})</span>}
                      </div>
                    ))}
                  </div>
                </CVSection>
              )}

              {/* Completed Goals */}
              {data.goals.length > 0 && (
                <CVSection title="Goals Achieved" icon={Target}>
                  <ul className="space-y-1.5">
                    {data.goals.map((g, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                        <Star className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                        {g}
                      </li>
                    ))}
                  </ul>
                </CVSection>
              )}

              {/* Achievements */}
              {data.achievements.length > 0 && (
                <CVSection title="Achievements" icon={Award}>
                  <div className="flex flex-wrap gap-2">
                    {data.achievements.map((a, i) => <Tag key={i}>{a}</Tag>)}
                  </div>
                </CVSection>
              )}

              {/* Extracurriculars */}
              {data.extracurriculars.length > 0 && (
                <CVSection title="Extracurricular Activities" icon={GraduationCap}>
                  <div className="flex flex-wrap gap-2">
                    {data.extracurriculars.map((e, i) => <Tag key={i}>{e}</Tag>)}
                  </div>
                </CVSection>
              )}

              {/* Footer */}
              <div className="mt-8 pt-4 border-t border-slate-100 text-center text-xs text-slate-300 print:text-slate-400">
                Generated by EarlyScape · earlyscape.io
              </div>
            </div>

          </div>
        </main>
      </div>

      <style>{`
        @media print {
          .lg\\:ml-64, nav, aside, .print\\:hidden { display: none !important; }
          main { margin: 0 !important; padding: 0 !important; }
          body { background: white !important; }
        }
      `}</style>
    </ProtectedRoute>
  )
}
